// modules/assinaturas/estabelecimento/List/List.tsx
import { useEffect, useMemo, useState } from 'react';
import AvatarUser from '../../../../components/AvatarUser';
import { useParamsContext } from '../../../../contexts/ParamsContext';
import { normalizeSearch } from '../../../recorrencias/helpers';
import { useAssinaturasContext } from '../Context';
import { BillingStatusTag, formatCivilDateBR, formatCurrencyBRL, formatDateBR } from '../utils/periodosUtils';
import '../Assinaturas.css';
import { ChevronRight, CircleCheck, FilePen, Search, TriangleAlert } from 'lucide-react';

type Aba = 'ativas' | 'vencidas' | 'canceladas';

const PAGE_SIZE = 40;

const getClientName = (row: any) =>
    row.clientName || row.clienteNome || row.cliente?.name || row.client?.name || '';

const getPlanName = (row: any) =>
    row.planName || row.planoNome || row.plano?.name || row.plan?.name || '';

const isVencida = (row: any) => row.status === 1 && String(row.lastBillingStatus || '').toLowerCase() === 'vencido';

const pertenceAba = (row: any, aba: Aba) => {
    if (aba === 'canceladas') return row.status !== 1;
    if (aba === 'vencidas') return isVencida(row);
    return row.status === 1;
};

const nextBillingValue = (row: any) => (row.nextBillingAt ? new Date(row.nextBillingAt).getTime() : Infinity);

const AssinaturaRow = ({ row }: { row: any }) => {
    const { setParams } = useParamsContext();
    const clientName = getClientName(row) || 'Cliente não identificado';
    const isUnico = row.billingType === 'UNICO';

    return (
        <li>
            <button
                type="button"
                className="ass-row"
                onClick={() => setParams({ action: 'periodos', id: row._id })}
            >
                <AvatarUser name={clientName} photoURL={undefined} size="h-10 w-10 !text-sm" />
                <span className="ass-cell ass-cell-main">
                    <span className="ass-title">{clientName}</span>
                    <span className="ass-sub">{getPlanName(row) || 'Plano não informado'}</span>
                </span>
                <span className="ass-cell">
                    <span className="ass-cell-label">Próxima cobrança</span>
                    <span className="ass-text">
                        {row.status !== 1 ? 'Cancelada' : isUnico ? 'Pagamento único' : formatCivilDateBR(row.nextBillingAt)}
                    </span>
                </span>
                <span className="ass-cell">
                    <span className="ass-cell-label">Último pagamento</span>
                    <span className="ass-payment">
                        <span className="ass-text">
                            {row.lastPaymentAt
                                ? `${formatCurrencyBRL(row.lastPaymentAmount)} em ${formatDateBR(row.lastPaymentAt)}`
                                : 'Nenhum'}
                        </span>
                        {!!row.lastBillingStatus && <BillingStatusTag status={row.lastBillingStatus} />}
                    </span>
                </span>
                <span className="ass-cell ass-cell-price">
                    <span className="ass-cell-label">Valor</span>
                    <span className="ass-price">
                        {formatCurrencyBRL(row.price ?? row.valor ?? row.plano?.price ?? row.plan?.price ?? 0)}
                    </span>
                </span>
                <ChevronRight className="ass-chevron" aria-hidden />
            </button>
        </li>
    );
};

export default function AssinaturasList() {
    const { data, loading, error, reloadData } = useAssinaturasContext();
    const { setParams } = useParamsContext();
    const [aba, setAba] = useState<Aba>('ativas');
    const [search, setSearch] = useState('');
    const [visible, setVisible] = useState(PAGE_SIZE);

    // A busca vale para todas as abas: a contagem mostra onde esta o resultado.
    const filtradas = useMemo(() => {
        const term = normalizeSearch(search);
        if (!term) return data;

        return data.filter((row) => normalizeSearch(`${getClientName(row)} ${getPlanName(row)}`).includes(term));
    }, [data, search]);

    const lista = useMemo(() => {
        const rows = filtradas.filter((row) => pertenceAba(row, aba));

        return aba === 'canceladas'
            ? rows
            : [...rows].sort((a, b) => nextBillingValue(a) - nextBillingValue(b));
    }, [filtradas, aba]);

    useEffect(() => {
        setVisible(PAGE_SIZE);
    }, [aba, search]);

    if (loading) {
        return (
            <div className="space-y-2 px-2" aria-busy="true">
                <div className="sf-skeleton h-10" />
                {[0, 1, 2, 3, 4].map((item) => (
                    <div key={item} className="sf-skeleton h-16" />
                ))}
            </div>
        );
    }

    if (error && !data.length) {
        return (
            <div className="sf-state">
                <TriangleAlert aria-hidden />
                <h2>Não foi possível carregar as assinaturas</h2>
                <button type="button" className="btn btn-primary btn-sm" onClick={() => void reloadData()}>
                    Tentar novamente
                </button>
            </div>
        );
    }

    if (!data.length) {
        return (
            <div className="sf-state">
                <FilePen aria-hidden />
                <h2>Nenhuma assinatura</h2>
                <p>Vincule um cliente a um plano para acompanhar cobranças e uso dos serviços.</p>
                <button type="button" className="btn btn-primary btn-sm" onClick={() => setParams({ action: 'add' })}>
                    Nova assinatura
                </button>
            </div>
        );
    }

    const abas: { key: Aba; label: string }[] = [
        { key: 'ativas', label: 'Ativas' },
        { key: 'vencidas', label: 'Vencidas' },
        { key: 'canceladas', label: 'Canceladas' },
    ];

    return (
        <div className="ass-page ass-list px-2">
            <div className="ass-toolbar">
                <div className="sf-segmented" role="group" aria-label="Situação das assinaturas">
                    {abas.map(({ key, label }) => (
                        <button key={key} type="button" aria-pressed={aba === key} onClick={() => setAba(key)}>
                            {label}
                            <span className="sf-segmented-count">
                                {filtradas.filter((row) => pertenceAba(row, key)).length}
                            </span>
                        </button>
                    ))}
                </div>
                <label className="sf-search">
                    <Search aria-hidden />
                    <input
                        type="search"
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                        placeholder="Buscar cliente ou plano"
                        aria-label="Buscar cliente ou plano"
                    />
                </label>
            </div>

            {lista.length === 0 ? (
                <div className="sf-state">
                    {search ? <Search aria-hidden /> : <CircleCheck aria-hidden />}
                    <h2>
                        {search
                            ? 'Nenhum resultado nesta aba'
                            : aba === 'vencidas'
                                ? 'Nenhuma cobrança vencida'
                                : aba === 'canceladas'
                                    ? 'Nenhuma assinatura cancelada'
                                    : 'Nenhuma assinatura ativa'}
                    </h2>
                </div>
            ) : (
                <>
                    <div className="ass-head" aria-hidden>
                        <span>Cliente</span>
                        <span>Próxima cobrança</span>
                        <span>Último pagamento</span>
                        <span className="text-right">Valor</span>
                    </div>
                    <ul className="sf-group">
                        {lista.slice(0, visible).map((row) => (
                            <AssinaturaRow key={row._id} row={row} />
                        ))}
                    </ul>
                    {lista.length > visible && (
                        <div className="mt-4 flex justify-center">
                            <button
                                type="button"
                                className="sf-link"
                                onClick={() => setVisible((current) => current + PAGE_SIZE)}
                            >
                                Mostrar mais ({lista.length - visible})
                            </button>
                        </div>
                    )}
                </>
            )}
        </div>
    );
}
